import { Tenant } from './api/model/tenant';
import { NewRelicAgentConfig } from './newRelicHelper';

export type ContentAreaDetails = {
  name: string;
  derivedCultures: any;
  contentAuthoringAccountId: string;
  contentAuthoringAreaId: string;
};

export type ContentAreaIdConfiguration = { [areaId: string]: ContentAreaDetails };
export type ContentAccountIdConfiguration = { name: string; area: ContentAreaIdConfiguration };

interface NewRelicSettings {
  agentEnabled: boolean;
  agentConfig: NewRelicAgentConfig;
}

interface Config {
  newRelicSettings: NewRelicSettings;
  //Three letter environment abbreviation as required in Yeti tags
  threeLetterEnvironment: string;
  localizationUrl: string;
  localizationUIServerUrl: string;
  deliveryServiceUrl: string;
  camsUrl: string;
  camUrl: string;
}

export const defaultTenant: Tenant = { accountId: 'ozoDdrmewShEcbUDWX8J3V', areaId: 'IoBX4KKMc0uyUyA0tFQDg' };
export const getDefaultPath = (accountId: string, areaId: string) =>
  `/Accounts/${accountId}/ContentArea/${areaId}/Batches`;
const newRelicBrowserIngestKey = 'NRBR-8406f46b115766c61a4';

const mockAccountsConfiguration: { [accountId: string]: ContentAccountIdConfiguration } = {
  ozoDdrmewShEcbUDWX8J3V: {
    name: 'Vistaprint',
    area: {
      IoBX4KKMc0uyUyA0tFQDg: {
        name: 'Product Design',
        contentAuthoringAccountId: 'ozoDdrmewShEcbUDWX8J3V',
        contentAuthoringAreaId: 'BariCgbVOzbaYywM',
        derivedCultures: {
          'de-de': ['de-at', 'de-ch'],
          'en-gb': ['en-ie', 'en-au', 'en–sg'],
          'fr-fr': ['fr-be'],
          'it-it': ['it-ch'],
          'nl-nl': ['nl-be'],
        },
      },
    },
  },
  g2Ez5VaoZWoqU22XqPjTLU: {
    name: 'Cimpress',
    area: {
      V0DJMs5nByPR6UD: {
        name: 'Content Solution Test',
        contentAuthoringAccountId: 'g2Ez5VaoZWoqU22XqPjTLU',
        contentAuthoringAreaId: '5EUcWSDo40YEAt3O',
        derivedCultures: {},
      },
      T8GQCedgukeoaqvsh2Zp2g: {
        name: 'Content Automation Test',
        contentAuthoringAccountId: 'g2Ez5VaoZWoqU22XqPjTLU',
        contentAuthoringAreaId: 'HzdcFkOSpddfFjSR',
        derivedCultures: {},
      },
    },
  },
};

const local: Config = {
  newRelicSettings: {
    agentEnabled: false,
    agentConfig: {
      init: {
        distributed_tracing: { enabled: true },
        privacy: { cookies_enabled: true },
        ajax: { deny_list: ['bam.nr-data.net'] },
      },
      info: {
        beacon: 'bam.nr-data.net',
        errorBeacon: 'bam.nr-data.net',
        licenseKey: newRelicBrowserIngestKey,
        applicationID: '1120401709',
        sa: 1,
      },
      loader_config: {
        accountID: '1328207',
        trustKey: '1328207',
        agentID: '1120401709',
        licenseKey: newRelicBrowserIngestKey,
        applicationID: '1120401709',
      },
    },
  },
  threeLetterEnvironment: 'loc',
  localizationUrl: 'https://dev.localization.content.cimpress.io',
  localizationUIServerUrl: 'https://localhost:5001',
  deliveryServiceUrl: 'https://dev.delivery.content.cimpress.io',
  camsUrl: 'https://dev.cams.contentauthoring.cimpress.io',
  camUrl: 'https://dev.cam.contentauthoring.cimpress.io',
};

const dev: Config = {
  newRelicSettings: {
    agentEnabled: true,
    agentConfig: {
      init: {
        distributed_tracing: { enabled: true },
        privacy: { cookies_enabled: true },
        ajax: { deny_list: ['bam.nr-data.net'] },
      },
      info: {
        beacon: 'bam.nr-data.net',
        errorBeacon: 'bam.nr-data.net',
        licenseKey: newRelicBrowserIngestKey,
        applicationID: '1120401709',
        sa: 1,
      },
      loader_config: {
        accountID: '1328207',
        trustKey: '1328207',
        agentID: '1120401709',
        licenseKey: newRelicBrowserIngestKey,
        applicationID: '1120401709',
      },
    },
  },
  threeLetterEnvironment: 'dev',
  localizationUrl: 'https://dev.localization.content.cimpress.io',
  localizationUIServerUrl: 'https://dev.localization.contentauthoring.cimpress.io',
  deliveryServiceUrl: 'https://dev.delivery.content.cimpress.io',
  camsUrl: 'https://dev.cams.contentauthoring.cimpress.io',
  camUrl: 'https://dev.cam.contentauthoring.cimpress.io',
  //localizationUIServerUrl: "https://localhost:5001"
};

const prod: Config = {
  newRelicSettings: {
    agentEnabled: true,
    agentConfig: {
      init: {
        distributed_tracing: { enabled: true },
        privacy: { cookies_enabled: true },
        ajax: { deny_list: ['bam.nr-data.net'] },
      },
      info: {
        beacon: 'bam.nr-data.net',
        errorBeacon: 'bam.nr-data.net',
        licenseKey: newRelicBrowserIngestKey,
        applicationID: '1120401711',
        sa: 1,
      },
      loader_config: {
        accountID: '1328207',
        trustKey: '1328207',
        agentID: '1120401711',
        licenseKey: newRelicBrowserIngestKey,
        applicationID: '1120401711',
      },
    },
  },
  threeLetterEnvironment: 'prd',
  localizationUrl: 'https://prod.localization.content.cimpress.io',
  localizationUIServerUrl: 'https://localization.contentauthoring.cimpress.io',
  deliveryServiceUrl: 'https://prod.delivery.content.cimpress.io',
  camsUrl: 'https://prod.cams.contentauthoring.cimpress.io',
  camUrl: 'https://cam.contentauthoring.cimpress.io',
};

const config =
  process.env.REACT_APP_STAGE === 'production' ? prod : process.env.REACT_APP_STAGE === 'development' ? dev : local;

export default { ...config, mockAccountsConfiguration };
